import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { Icon } from "@/react/components/shared/Icon";
import { Typography } from "@circle-react-uikit/Typography";

interface HelpInputAreaProps {
  allowAttachments?: boolean;
  attachments?: { url: string; filename: string; completed: boolean }[];
  isBotProcessing?: boolean;
  onSubmit: (text: string) => void;
  placeholder?: string;
  uploadAttachments?: (files: File[]) => void;
}

export const HelpInputArea = ({
  onSubmit,
  placeholder = "",
  allowAttachments = false,
  uploadAttachments,
  attachments = [],
  isBotProcessing = false,
}: HelpInputAreaProps) => {
  const [inputText, setInputText] = useState("");

  const areAllAttachmentsComplete = attachments.every(
    attachment => attachment.completed,
  );

  const hasText = inputText.trim().length > 0;
  const isSubmitEnabled =
    !isBotProcessing &&
    (hasText || (attachments.length > 0 && areAllAttachmentsComplete));

  const handleSubmit = () => {
    if (isSubmitEnabled) {
      onSubmit(inputText.trim());
      setInputText("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const { getRootProps, getInputProps, inputRef } = useDropzone({
    onDrop: uploadAttachments,
    noClick: true,
    disabled: !allowAttachments,
  });

  const openFilePicker = (event: React.MouseEvent) => {
    event.preventDefault();
    inputRef.current?.click();
  };

  return (
    <div
      className="relative w-full rounded-xl border-2 bg-white px-4 py-3 shadow-sm hover:border-2 hover:border-[#AD89DC]"
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <Typography.BodySm as="div">
        <textarea
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="h-[78px] min-h-[60px] w-full resize-none border-none bg-transparent p-0 align-middle placeholder:text-[rgba(113,118,128,1)] focus:outline-none focus:ring-0"
          rows={2}
        />
      </Typography.BodySm>
      {allowAttachments && (
        <button
          type="button"
          onClick={openFilePicker}
          className="absolute bottom-4 left-4 flex h-6 w-6 items-center justify-center"
        >
          <Icon type="hw-paperclip" size={20} />
        </button>
      )}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!isSubmitEnabled}
        className="absolute bottom-3 right-4 flex h-[28px] w-[28px] items-center justify-center"
      >
        <Icon
          type={
            isSubmitEnabled
              ? "sw-help-submit-enabled"
              : "sw-help-submit-disabled"
          }
          size={28}
          className="!w-[28px]"
        />
      </button>
    </div>
  );
};
